/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 01, 2023
 **/

import { EventHandler } from "@rfice/eventhandler";
import { Environments } from "@/frameworks/libraries/utilities";
import { Configure } from "@/frameworks/libraries/utilities";

import { EventID } from "../commons";

const LOGTAG = "#useUnityCommand#";

export const send_message = <T = Message.Frame>(data: T) => {
	EventHandler.fire("$Unity::SendMessage$", data);
	// console.debug(LOGTAG, "send message to unity", data);
};

export default function useUnityCommand() {
	const systemCommand = {
		activate() {
			EventHandler.emit(EventID.EXEC_UNITY_PLAYER);
		},
		sleep() {
			EventHandler.emit(EventID.SLEEP_UNITY_PLAYER);
		},
		deactivate() {
			EventHandler.emit(EventID.CLOSE_UNITY_PLAYER);
		}
	};
	/**
	 * bypass to Unity
	 */
	const rawCommand = {
		sendMessage(payload: Message.Frame) {
			send_message(payload);
		}
	};
	const ipcCommand = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		request(id: string, data: any) {
			send_message({ type: "request", payload: { id, data } });
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		response(id: string, data: any) {
			send_message({ type: "response", payload: { id, data } });
		},
		receiveMessage({ /*type, */ payload }: Message.Frame) {
			send_message({ type: "receiveMessage", payload });
		},
		changeSpace(payload: IPC.Spaces.ChangeSpace) {
			send_message({
				type: "changeSpace",
				payload
			});
		},
		changePortalSpace(payload: IPC.Spaces.ChangeSpace) {
			send_message({
				type: "changePortalSpace",
				payload
			});
		},
		/**
		 * 값이 false일때 유니티에서 동작하지 않습니다.
		 */
		focusStatus(payload: IPC.Actions.FocusStatus) {
			send_message({ type: "focusStatus", payload });
		},
		tabStatus(payload: IPC.Actions.TabStatus) {
			send_message({ type: "tabStatus", payload });
		},
		requestRenderTexture(target: string, parts?: string) {
			send_message({
				type: "requestRenderTexture",
				payload: {
					renderTarget: target,
					...(parts ? { renderParts: parts } : {})
				}
			});
		},
		sendChatMessage(payload: IPC.Actions.ChatMessage) {
			send_message({ type: "sentChatMessage", payload });
		},
		screenModeChanged(mode: 0 | 1) {
			send_message({ type: "screenModeChanged", payload: { mode } });
		},
		yourLocation(payload: IPC.Spaces.YourLocation) {
			const { requesterID, deviceKey } = payload;
			send_message({ type: "yourLocation", payload: { requesterID, deviceKey } });
		},
		friendLocation(payload: IPC.Spaces.FriendLocation) {
			send_message({ type: "friendLocation", payload });
		},
		deviceStatus(payload: IPC.Devices.DeviceStatus) {
			send_message({ type: "deviceStatus", payload });
		},
		changeLanguage(payload: IPC.Actions.ChangeLanguage) {
			send_message({ type: "changeLanguage", payload });
		},
		updateUserProfile(payload: IPC.Actions.UpdateUserProfile) {
			send_message({ type: "updateUserProfile", payload });
		},
		changeCameraStatus(value: boolean) {
			send_message({
				type: "cameraStatus",
				payload: {
					enabled: value
				}
			});
		},
		changeObjectRatio(payload: IPC.Actions.ChangeObjectRatio) {
			send_message({ type: "changeObjectRatio", payload });
		},
		objectLinkChange(payload: IPC.Actions.objectLinkChange) {
			send_message({ type: "objectLinkChanged", payload });
		},
		startLogger(payload: IPC.Commons.StartLogger) {
			send_message({ type: "startLogger", payload });
		},
		logout() {
			send_message({ type: "logOut" });
		},
		checkUnitySoundVolume() {
			send_message({ type: "requestUnitySoundVolume" });
		},
		changeUnitySoundVolume(payload: IPC.Commons.UnitySoundVolume) {
			send_message({ type: "unitySoundVolume", payload });
		},
		uploadFileToUnity(payload: IPC.Spaces.UploadFileToUnity) {
			send_message({ type: "uploadFileToUnity", payload });
		},
		uploadLinkToUnity(payload: IPC.Spaces.UploadLinkToUnity) {
			send_message({ type: "uploadLinkToUnity", payload });
		},
		sendPopupFeedback(payload: IPC.Actions.PopupFeedBack) {
			send_message({ type: "popupFeedback", payload });
		},
		voiceCallState(payload: IPC.Actions.VoiceCallState) {
			send_message({ type: "voiceCallState", payload });
		},
		userGesture(payload: IPC.Actions.UserGesture) {
			send_message({ type: "userGesture", payload });
			Configure.useLogLevel >= 6 && console.debug(`${LOGTAG} fire user gesture with`, payload);
		},
		spaceOptionChanged(payload: IPC.Spaces.SpaceOptionsChanged) {
			send_message({ type: "spaceOptionsChanged", payload });
		},
		callState(payload: IPC.Actions.CallState) {
			send_message({ type: "callState", payload });
		},
		backgroundSoundState(payload: IPC.Actions.BackgroundSoundState) {
			send_message({ type: "backgroundSoundState", payload });
		},
		useMydesk(payload: IPC.Spaces.UseMyDesk) {
			send_message({ type: "useMyDesk", payload });
		},
		graphicQualityState(payload: IPC.Devices.GraphicQualityState) {
			send_message({ type: "graphicQualityState", payload });
		},
		stopSharingObject(payload: IPC.Actions.StopSharingObject) {
			send_message({ type: "stopSharingObject", payload });
		},
		spaceParticipated() {
			send_message({ type: "spaceParticipated" });
		},
		fileUploadProgress(payload: IPC.Actions.FileUploadProgress) {
			send_message({ type: "fileUploadProgress", payload });
		},
		videoChatRecordState(payload: IPC.Actions.VideoChatRecordState) {
			send_message({ type: "videoChatRecordState", payload });
		},
		fileUploadStart(payload: IPC.Actions.fileUploadStart) {
			send_message({ type: "fileUploadStart", payload });
		},
		useWhiteboard(payload: IPC.Actions.UseWhiteboard) {
			send_message({ type: "useWhiteboard", payload });
		},
		useFEFloatingList(payload: IPC.Actions.UseWhiteboard) {
			send_message({ type: "useFEFloatingList", payload });
		},
		useFECameraFeed(payload: IPC.Actions.UseWhiteboard) {
			send_message({ type: "useFECameraFeed", payload });
		},
		changeToken(payload: IPC.Commons.ChangeToken) {
			send_message({ type: "changeToken", payload });
		},
		closeWhiteboard(payload: IPC.Actions.CloseWhiteboard) {
			send_message({ type: "closeWhiteboard", payload });
		},
		whiteboardOwnerChanged(payload: IPC.Actions.WhiteboardOwnerChanged) {
			send_message({ type: "whiteboardOwnerChanged", payload });
		},
		fileRemoved(payload: IPC.Actions.FileRemoved) {
			send_message({ type: "fileRemoved", payload });
		},
		runBackgroundUnityProcess(payload: IPC.Commons.RunBackgroundUnityProcess) {
			send_message({ type: "runBackgroundUnityProcess", payload });
		},
		sleepMode() {
			send_message({ type: "terminateSpace" });
		},
		fileUploadToTv(payload: IPC.Spaces.UploadFileToUnity) {
			send_message({ type: "fileUploadToTv", payload });
		},
		screenSharingUploadToTv(payload: IPC.Spaces.ScreenSharingUploadToTv) {
			send_message({ type: "screenSharingUploadToTv", payload });
		},
		startSharingTheScreen(payload: IPC.Spaces.StartSharingTheScreen) {
			send_message({ type: "startSharingTheScreen", payload });
		}
	};

	return {
		...systemCommand,
		...ipcCommand,
		...rawCommand
	};
}

export function UNITY_BASE_URI() {
	if (location.host.startsWith("localhost")) {
		// TODO 모듈 분리 시 분리된 모듈명으로 변경해야 함
		// @ts-ignore
		return `/unity`;
	}
	const url = Environments.env<string | null>("UNITY_BASE_URI") as string;
	if (url.startsWith("http")) {
		return url;
	} else if (url.startsWith("//")) {
		return `https:${url}`;
	}
	return url;
}

export function UNITY_ASSET_URI() {
	if (location.host.startsWith("localhost")) {
		// TODO 모듈 분리 시 분리된 모듈명으로 변경해야 함
		// @ts-ignore
		return `/unity`;
	}
	const url = Environments.env<string | null>("UNITY_ASSET_URI") as string;
	if (url.startsWith("http")) {
		return url;
	} else if (url.startsWith("//")) {
		return `https:${url}`;
	}
	return url;
}
